/**
 * `/payroll` — the trainer payroll board (migration 152).
 *
 * One run per calendar month: the engine's per-trainer component breakdown
 * (session revenue share / hourly / PR fixed / the two bonus pools /
 * adjustments), the data-gaps panel (everything the computation could NOT
 * attribute — placeholder rates, unknown slugs, unattributable sessions),
 * and the lifecycle buttons. Mutations dispatch through the generic
 * `POST /api/dispatch/:action` (create/recompute/approve) — the same
 * chokepoint the CLI uses; approving here is Mario's freeze.
 */

import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  api,
  ApiError,
  type PayrollBoardResponse,
  type PayrollRunSummary,
} from "../lib/api";
import { dispatchAction } from "../lib/dispatch";
import { studioFormat } from "../lib/studio-tz";

const DATETIME_OPTS: Intl.DateTimeFormatOptions = {
  month: "short",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
};

function fmtDateTime(iso: string | null): string {
  if (iso === null) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : studioFormat(DATETIME_OPTS).format(d);
}

function monthLabel(periodMonth: string): string {
  const d = new Date(`${periodMonth}T12:00:00Z`);
  return Number.isNaN(d.getTime())
    ? periodMonth
    : new Intl.DateTimeFormat("en-GB", {
        month: "long",
        year: "numeric",
        timeZone: "UTC",
      }).format(d);
}

function pounds(minor: number): string {
  return `£${(minor / 100).toFixed(2)}`;
}

const COMPONENT_LABELS: Readonly<Record<string, string>> = {
  session_revenue_share: "Revenue share",
  hourly: "Hourly",
  pr_session_fixed: "PR sessions",
  unpriced_session_fixed: "Unpriced sessions (flat rate)",
  bonus_new_signup: "Bonus · new sign-up",
  bonus_renewal: "Bonus · renewal",
  adjustment: "Adjustment",
};

export function Payroll(): JSX.Element {
  const [data, setData] = useState<PayrollBoardResponse | null>(null);
  const [month, setMonth] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const reload = useCallback((): void => {
    setLoading(true);
    api
      .payrollBoard(month)
      .then((b) => {
        setData(b);
        setError(null);
      })
      .catch((e: unknown) => {
        setError(e instanceof ApiError ? e.message : String(e));
      })
      .finally(() => setLoading(false));
  }, [month]);

  useEffect(() => {
    reload();
  }, [reload]);

  async function act(
    label: string,
    action: string,
    body: Record<string, unknown>,
  ): Promise<void> {
    setBusy(label);
    setNotice(null);
    try {
      await dispatchAction(action, body);
      setNotice(`${label} — done.`);
      reload();
    } catch (e) {
      setNotice(
        `${label} failed: ${e instanceof ApiError ? e.message : String(e)}`,
      );
    } finally {
      setBusy(null);
    }
  }

  if (loading && data === null) return <p className="empty">Loading payroll…</p>;
  if (error !== null) {
    return <p className="empty">Couldn&apos;t load payroll ({error}).</p>;
  }
  if (data === null) return <p className="empty">No data.</p>;

  const { runs, selected, lines, adjustments } = data;

  if (selected === null) {
    return (
      <>
        <h1 className="page-title">Payroll</h1>
        <p className="empty">
          No payroll run yet — the monthly cron drafts one on the 1st, or
          create one now.
        </p>
        <button
          type="button"
          className="pr-toggle"
          disabled={busy !== null}
          onClick={() => {
            const now = new Date();
            const prior = new Date(
              Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1),
            );
            const m = `${prior.getUTCFullYear()}-${String(prior.getUTCMonth() + 1).padStart(2, "0")}`;
            void act("Create draft", "create_payroll_run", { month: m });
          }}
        >
          Create last month&apos;s draft
        </button>
        {notice !== null ? <p className="subtle">{notice}</p> : null}
      </>
    );
  }

  const trainers = Object.entries(selected.totals).sort(
    ([, a], [, b]) => b.total_minor - a.total_minor,
  );
  const nameFor = (trainerId: string): string =>
    lines.find((l) => l.trainer_id === trainerId)?.trainer_display_name ??
    trainerId.slice(0, 8);
  const runMonth = selected.period_month.slice(0, 7);

  return (
    <>
      <div className="pr-section-head">
        <h1 className="page-title">
          Payroll — {monthLabel(selected.period_month)}
        </h1>
        {runs.length > 1 ? (
          <select
            value={month ?? runMonth}
            onChange={(e) => setMonth(e.target.value)}
            aria-label="Payroll month"
          >
            {runs.map((r: PayrollRunSummary) => (
              <option key={r.payroll_run_id} value={r.period_month.slice(0, 7)}>
                {monthLabel(r.period_month)}
              </option>
            ))}
          </select>
        ) : null}
      </div>

      <div className="pr-overview">
        <div className="pr-stat">
          <div className="pr-stat-value">{selected.status}</div>
          <div className="pr-stat-label">status</div>
        </div>
        <div className="pr-stat">
          <div className="pr-stat-value">
            {pounds(
              trainers.reduce((a, [, t]) => a + t.total_minor, 0),
            )}
          </div>
          <div className="pr-stat-label">total</div>
        </div>
        <div className="pr-stat">
          <div className="pr-stat-value">{trainers.length}</div>
          <div className="pr-stat-label">trainers</div>
        </div>
        <div className="pr-stat">
          <div className="pr-stat-value">{selected.data_gaps.length}</div>
          <div className="pr-stat-label">data gaps</div>
        </div>
      </div>
      <p className="subtle pr-overview-note">
        computed {fmtDateTime(selected.computed_at)}
        {selected.approved_at !== null
          ? ` · approved ${fmtDateTime(selected.approved_at)} by ${selected.approved_by ?? "?"}`
          : ""}
        {selected.delivered_at !== null
          ? ` · delivered ${fmtDateTime(selected.delivered_at)}`
          : ""}
        . Approving freezes the month — corrections then target the next run.
      </p>

      <div className="pr-section-head">
        <h2 className="pr-heading">Actions</h2>
        {notice !== null ? <span className="subtle">{notice}</span> : null}
      </div>
      <p>
        <button
          type="button"
          className="pr-toggle"
          disabled={busy !== null || selected.status !== "draft"}
          onClick={() =>
            void act("Recompute", "recompute_payroll_run", {
              payroll_run_id: selected.payroll_run_id,
              month: runMonth,
            })
          }
        >
          Recompute draft
        </button>{" "}
        <button
          type="button"
          className="pr-toggle"
          disabled={
            busy !== null ||
            (selected.status !== "draft" && selected.status !== "in_review")
          }
          onClick={() =>
            void act("Approve", "approve_payroll_run", {
              payroll_run_id: selected.payroll_run_id,
            })
          }
        >
          ✅ Approve (freeze month)
        </button>
      </p>

      <div className="pr-section-head">
        <h2 className="pr-heading">Statements</h2>
        <span className="subtle">{lines.length} lines</span>
      </div>
      {trainers.length === 0 ? (
        <p className="empty">
          No payable lines — check the data gaps below (placeholder rates
          land there until filled in the studio config).
        </p>
      ) : (
        <div className="table-scroll-x">
          <table className="pr-people-table table-cards">
            <thead>
              <tr>
                <th>Trainer</th>
                <th>Component</th>
                <th className="pr-num">Qty</th>
                <th className="pr-num">Amount</th>
                <th>Rate</th>
              </tr>
            </thead>
            <tbody>
              {trainers.flatMap(([trainerId, totals]) => {
                const mine = lines.filter((l) => l.trainer_id === trainerId);
                const rows = mine.map((l, i) => (
                  <tr key={`${trainerId}-${l.component}-${i}`}>
                    <td className="table-card-title">
                      {i === 0 ? (
                        <Link to={`/trainers/${encodeURIComponent(trainerId)}`}>
                          {l.trainer_display_name}
                        </Link>
                      ) : (
                        ""
                      )}
                    </td>
                    <td data-label="Component">{COMPONENT_LABELS[l.component] ?? l.component}</td>
                    <td data-label="Qty" className="pr-num">{l.quantity}</td>
                    <td data-label="Amount" className="pr-num">{pounds(l.amount_minor)}</td>
                    <td data-label="Rate" className="dc-muted">{JSON.stringify(l.rate)}</td>
                  </tr>
                ));
                rows.push(
                  <tr key={`${trainerId}-total`}>
                    <td className="table-card-title">{mine.length === 0 ? nameFor(trainerId) : ""}</td>
                    <td data-label="Component">
                      <b>Total</b>
                      {totals.no_show_sessions > 0 ? (
                        <span
                          className="tag"
                          title="No-show sessions included per policy"
                        >
                          {totals.no_show_sessions} no-show
                        </span>
                      ) : null}
                    </td>
                    <td data-label="Qty" className="pr-num">{totals.payable_sessions}</td>
                    <td data-label="Amount" className="pr-num">
                      <b>{pounds(totals.total_minor)}</b>
                    </td>
                    <td data-label="Rate" />
                  </tr>,
                );
                return rows;
              })}
            </tbody>
          </table>
        </div>
      )}

      {adjustments.length > 0 ? (
        <>
          <div className="pr-section-head">
            <h2 className="pr-heading">Adjustments</h2>
          </div>
          <div className="table-scroll-x">
            <table className="pr-people-table table-cards">
              <tbody>
                {adjustments.map((a) => (
                  <tr key={a.id}>
                    <td className="table-card-title">{a.trainer_display_name}</td>
                    <td data-label="Amount" className="pr-num">{pounds(a.amount_minor)}</td>
                    <td data-label="Reason">{a.reason}</td>
                    <td data-label="By" className="dc-muted">
                      {a.created_by} · {fmtDateTime(a.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}

      <div className="pr-section-head">
        <h2 className="pr-heading">Data gaps</h2>
        <span className="subtle">what the numbers omit</span>
      </div>
      {selected.data_gaps.length === 0 ? (
        <p className="empty">None — every session attributed.</p>
      ) : (
        <div className="table-scroll-x">
          <table className="pr-people-table table-cards">
            <tbody>
              {selected.data_gaps.map((g, i) => (
                <tr key={i}>
                  <td className="table-card-title">
                    <span className="pr-type-badge">{g.kind}</span>
                  </td>
                  <td data-label="Detail">{g.detail}</td>
                  <td data-label="Rows" className="dc-muted">{g.refs.length} row(s)</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
